import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Button,
  message,
  Space,
  Typography,
  Alert,
  Divider
} from 'antd';
import {
  BugOutlined,
  ExclamationCircleOutlined,
  BulbOutlined,
  UploadOutlined,
  SendOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import apiService from '../services/api';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

interface ReportModalProps {
  visible: boolean;
  onCancel: () => void;
}

interface ReportFormData {
  type: 'bug' | 'error' | 'suggestion';
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high';
  images?: File[];
}

const ReportModal: React.FC<ReportModalProps> = ({ visible, onCancel }) => {
  const [form] = Form.useForm();
  const { user } = useCognitoAuth();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);

  const handleSubmit = async (values: ReportFormData) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('type', values.type);
      formData.append('title', values.title);
      formData.append('description', values.description);
      formData.append('priority', values.priority);
      formData.append('user_id', user?.id?.toString() || '');
      formData.append('username', user?.username || '');
      formData.append('user_email', user?.email || '');

      // Add image files
      fileList.forEach((file, index) => {
        if (file.originFileObj) {
          formData.append('images', file.originFileObj);
        }
      });

      const response = await apiService.submitReport(formData);
      
      if (response.success) {
        message.success('Relatório enviado com sucesso!');
        form.resetFields();
        setFileList([]);
        onCancel();
      } else {
        message.error(response.message || 'Erro ao enviar relatório');
      }
    } catch (error) {
      message.error('Erro ao enviar relatório. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    onCancel();
  };

  const uploadProps = {
    fileList,
    beforeUpload: () => false, // Prevent auto upload
    onChange: ({ fileList }: any) => {
      setFileList(fileList);
    },
    multiple: true,
    accept: 'image/*',
    listType: 'picture-card' as const,
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'bug':
        return <BugOutlined style={{ color: '#ff4d4f' }} />;
      case 'error':
        return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
      case 'suggestion':
        return <BulbOutlined style={{ color: '#52c41a' }} />;
      default:
        return <BugOutlined />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'bug':
        return '#ff4d4f';
      case 'error':
        return '#faad14';
      case 'suggestion':
        return '#52c41a';
      default:
        return '#1890ff';
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BugOutlined style={{ color: '#1890ff' }} />
          <span>Enviar Relatório</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={700}
      destroyOnClose
    >
      <Alert
        message="Ajuda-nos a melhorar o CRM!"
        description="Submete bugs, erros ou sugestões para melhorarmos o sistema. Todas as submissões são analisadas pela nossa equipa."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          type: 'bug',
          priority: 'medium'
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Form.Item
            name="type"
            label="Tipo de Relatório"
            rules={[{ required: true, message: 'Seleciona o tipo de relatório' }]}
          >
            <Select
              placeholder="Seleciona o tipo"
              onChange={(value) => {
                const icon = getTypeIcon(value);
                const color = getTypeColor(value);
                // Update form field with visual feedback
              }}
            >
              <Option value="bug">
                <Space>
                  <BugOutlined style={{ color: '#ff4d4f' }} />
                  Bug
                </Space>
              </Option>
              <Option value="error">
                <Space>
                  <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                  Erro
                </Space>
              </Option>
              <Option value="suggestion">
                <Space>
                  <BulbOutlined style={{ color: '#52c41a' }} />
                  Sugestão
                </Space>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="priority"
            label="Prioridade"
            rules={[{ required: true, message: 'Seleciona a prioridade' }]}
          >
            <Select placeholder="Seleciona a prioridade">
              <Option value="low">🟢 Baixa</Option>
              <Option value="medium">🟡 Média</Option>
              <Option value="high">🔴 Alta</Option>
            </Select>
          </Form.Item>
        </div>

        <Form.Item
          name="title"
          label="Título"
          rules={[
            { required: true, message: 'Introduz um título para o relatório' },
            { min: 5, message: 'O título deve ter pelo menos 5 caracteres' }
          ]}
        >
          <Input placeholder="Ex: Erro ao carregar lista de leads" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Descrição Detalhada"
          rules={[
            { required: true, message: 'Introduz uma descrição detalhada' },
            { min: 20, message: 'A descrição deve ter pelo menos 20 caracteres' }
          ]}
        >
          <TextArea
            rows={6}
            placeholder="Descreve o problema, erro ou sugestão em detalhes. Inclui passos para reproduzir se aplicável..."
          />
        </Form.Item>

        <Form.Item label="Imagens (Opcional)">
          <Upload {...uploadProps}>
            {fileList.length < 5 && (
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Adicionar Imagem</div>
              </div>
            )}
          </Upload>
          <Text type="secondary">
            Podes anexar até 5 imagens (PNG, JPG, GIF). Máximo 5MB por imagem.
          </Text>
        </Form.Item>

        <Divider />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleCancel} icon={<CloseOutlined />}>
            Cancelar
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<SendOutlined />}
          >
            Enviar Relatório
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default ReportModal;
